import React, { useState } from 'react';
import toastr from 'toastr';
import { FormattedMessage } from 'react-intl';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

import {
  Container,
  Col,
  Row,
  Button,
  Form,
  Card,
  ListGroup,
  ListGroupItem,
  Tabs,
  Tab,
} from 'react-bootstrap';
import JobPermittance from './JobPermittance';
import FarmPermittance from './FarmPermittance';
import { SentryPermittance } from './SentryPermittance';
import { RemoveUserAll } from './OrganizationEditHelper';
import DeleteWarningModal from './DeleteWarningModal';
import { Button as RaptorButton } from '@raptormaps/button';

import RestApiV2 from '../../api/RestApiV2';
import { useApi } from '../../hooks/useApi';
import { OrgsApi } from '@raptormaps/raptor-internal-client-ts';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';

//Models
import { Org } from '@raptormaps/raptor-models';
import type { OrgUser } from '../../api/types/OrgUser';
import { useUpdatePermissions } from '../../hooks/usePermissions';

const SAVING = 'SAVING';
const SAVED = 'SAVED';
interface OrganizationEditProps {
  org: Org;
  users: OrgUser[];
  getOrgUsers: () => Promise<void>;
  replaceUser: (user: OrgUser) => void;
  isLoading: boolean;
}

/**@class OrganizationEdit
 * This class serves as the parent class for the job permissions and the solar farm permissions.
 * Contains the tabs the allows the user to navigate from one to the other, displays the number
 * of total users in the org and allows the user to add more users to the org. */
const OrganizationEdit = (props: OrganizationEditProps) => {
  const { org, users, getOrgUsers, replaceUser } = props;

  const [selectedUser, setSelectedUser] = useState<OrgUser | undefined>();
  const [sentryButtonMode, setSentryButtonMode] = useState<string | null>(null);

  const [permissionButtonMode, setPermissionButtonMode] = useState<
    string | null
  >(null);
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addedUserEmail, setAddedUserEmail] = useState('');

  const [showRemoveAllModal, setShowRemoveAllModal] = useState<boolean>(false);
  const [showDeleteUserModal, setShowDeleteUserModal] =
    useState<boolean>(false);

  const useNewOrgPermissions = useFeatureIsOn('edit-org-permissions-v2');
  const { isFeatureEnabled } = useFeatureFlags(org.id);
  const orgHasWorkOrderFeatureFlag = isFeatureEnabled('WORK-ORDERS');
  const isSentryTabEnabled = useFeatureIsOn('org-users-sentry-permissions');

  const api = new RestApiV2();

  const OrgsApiClient = useApi(OrgsApi, {
    basePath: window.REACT_APP_API_ENDPOINT_V2,
  });

  const { mutateAsync: updateUserPermissions } = useUpdatePermissions(
    users,
    org.id,
  );

  /**Adds a user to the org, refreshes the org immediately after*/
  const addUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!addedUserEmail) return;

    setAddUserLoading(true);

    try {
      await OrgsApiClient.addOrgUser({
        add_org_user_input: {
          orgId: org.id,
          email: addedUserEmail.toLowerCase(),
        },
      });

      toastr.success(
        `User (${addedUserEmail}) has been successfully added and notified via email`,
      );

      setAddedUserEmail('');
      await getOrgUsers();
    } catch (err) {
      toastr.error(`Error adding user (${addedUserEmail}) to organization!`);
      console.log(err);
    }

    setAddUserLoading(false);
  };

  /**Removes a user from the org*/
  const removeUser = async () => {
    if (!selectedUser) return;

    try {
      const result = await OrgsApiClient.removeOrgUser({
        remove_org_user_input: {
          orgId: org.id,
          userId: selectedUser.id,
        },
      });

      await getOrgUsers();

      if (result.exit_status === 0) {
        toastr.success(
          `Successfully removed user ${selectedUser.email} from org`,
        );
      }
    } catch (err) {
      console.log(err);
    }
  };

  // Saves the permissions changed by the user.
  // but NOT sentry permissions.
  const savePermissions = async () => {
    setPermissionButtonMode(SAVING);

    // format data back to old version :melt:
    const oldFormatUsers = users.map(user => ({
      ...user,
      permittance: {
        user_id: user.id,
        org_id: org.id,
        can_view_jobs_by_default:
          user.permittance?.canViewJobsByDefault || false,
        can_download_jobs_by_default:
          user.permittance?.canDownloadJobsByDefault || false,
        can_edit_jobs_by_default:
          user.permittance?.canEditJobsByDefault || false,
        can_view_farms_by_default:
          user.permittance?.canViewFarmsByDefault || false,
        can_edit_farms_by_default:
          user.permittance?.canEditFarmsByDefault || false,
        can_manage_work_orders: user.permittance?.canManageWorkOrders || false,
        can_complete_work_orders:
          user.permittance?.canCompleteWorkOrders || false,
        can_upload_files: user.permittance?.canUploadFiles || false,
        can_delete_files: user.permittance?.canDeleteFiles || false,
        can_delete_jobs: user.permittance?.canDeleteJobs || false,
        can_create_jobs: user.permittance?.canCreateJobs || false,
        can_create_farms: user.permittance?.canCreateFarms || false,
        can_delete_farms: user.permittance?.canDeleteFarms || false,
        is_master: user.permittance?.isMaster || false,
        is_admin: user.permittance?.isAdmin || false,
      },
    }));

    try {
      await api.put(`/orgs/${org.id}/permittances`, {
        org_id: org.id,
        users: oldFormatUsers,
      });

      if (useNewOrgPermissions) {
        await OrgsApiClient.apiUpdateOrgPermittancesOrgsOrgIdPermittancesPut({
          org_id: org.id,
          org_permittance_request: {
            users: users.map(orgUser => ({
              permittance: {
                userId: orgUser.id,
                orgId: org.id,
                canViewJobsByDefault:
                  orgUser.permittance?.canViewJobsByDefault ?? false,
                canEditJobsByDefault:
                  orgUser.permittance?.canEditJobsByDefault ?? false,
                canDownloadJobsByDefault:
                  orgUser.permittance?.canDownloadJobsByDefault ?? false,
                canUploadFiles: orgUser.permittance?.canUploadFiles ?? false,
                canDeleteFiles: orgUser.permittance?.canDeleteFiles ?? false,
                canCreateJobs: orgUser.permittance?.canCreateJobs ?? false,
                canDeleteJobs: orgUser.permittance?.canDeleteJobs ?? false,
                isMaster: orgUser.permittance?.isMaster ?? false,
                isAdmin: orgUser.permittance?.isAdmin ?? false,
                canViewFarmsByDefault:
                  orgUser.permittance?.canViewFarmsByDefault ?? false,
                canEditFarmsByDefault:
                  orgUser.permittance?.canEditFarmsByDefault ?? false,
                canCreateFarms: orgUser.permittance?.canCreateFarms ?? false,
                canDeleteFarms: orgUser.permittance?.canDeleteFarms ?? false,
                canManageWorkOrders:
                  orgUser.permittance?.canManageWorkOrders ?? false,
                canCompleteWorkOrders:
                  orgUser.permittance?.canCompleteWorkOrders ?? false,
              },
            })),
          },
        });
      }

      setPermissionButtonMode(SAVED);
      toastr.success('Permissions have been successfully updated.');
    } catch (err) {
      toastr.error(JSON.stringify(err));
      console.log(err);
      setPermissionButtonMode('');
    }
  };

  // Saves the Sentry Command permissions changed by the user.
  const saveSentryPermissions = async () => {
    setSentryButtonMode(SAVING);
    try {
      await updateUserPermissions();
      setSentryButtonMode(SAVED);
    } catch (err) {
      toastr.error('Error saving Sentry permissions.');
      setSentryButtonMode(null);
    }
  };

  // This function updates the permissions for a specific user.
  // but NOT sentry permissions.
  const changePermissions = (userId: number, permission: string) => {
    setPermissionButtonMode(null);

    let selectedUser = users.find(user => user.id === userId);
    if (!selectedUser) return;
    if (!selectedUser.permittance) {
      selectedUser.permittance = {
        userId: selectedUser.id,
        orgId: org.id,
        canViewJobsByDefault: false,
        canDownloadJobsByDefault: false,
        canEditJobsByDefault: false,
        canViewFarmsByDefault: false,
        canEditFarmsByDefault: false,
        canManageWorkOrders: false,
        canCompleteWorkOrders: false,
        canUploadFiles: false,
        canDeleteFiles: false,
        canDeleteJobs: false,
        canCreateJobs: false,
        canCreateFarms: false,
        canDeleteFarms: false,
        isMaster: false,
        isAdmin: false,
      };
    }

    // Switch user's permission.
    // @ts-ignore
    selectedUser.permittance[permission] =
      // @ts-ignore
      !selectedUser.permittance[permission];

    // If user can download or edit jobs by default, they can also view.
    if (
      (permission === 'canDownloadJobsByDefault' ||
        permission === 'canEditJobsByDefault') &&
      selectedUser.permittance[permission] === true
    ) {
      selectedUser.permittance.canViewJobsByDefault = true;
    }

    // If they can't view by default, they can't download or edit.
    else if (
      permission === 'canViewJobsByDefault' &&
      selectedUser.permittance[permission] === false
    ) {
      selectedUser.permittance['canDownloadJobsByDefault'] = false;
      selectedUser.permittance['canEditJobsByDefault'] = false;
      setShowRemoveAllModal(true);
    }

    //farms permissions
    //if the edit permission is selected then the view is also selected.
    if (permission === 'canEditFarmsByDefault') {
      selectedUser.permittance['canViewFarmsByDefault'] = true;
    }

    //if view farms is de selected then the user cannot edit or manage work orders by default
    else if (
      permission === 'canViewFarmsByDefault' &&
      selectedUser.permittance[permission] === false
    ) {
      selectedUser.permittance['canEditFarmsByDefault'] = false;
      setShowRemoveAllModal(true);
    }

    replaceUser(selectedUser);
  };

  // Toggles only Sentry Command permissions for a user in the org
  const toggleSentryPermission = (userId: number, permission: string): void => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    if (permission === 'sentryPilot') {
      user.sentryPilot = !user.sentryPilot;
    }
    if (permission === 'sentryViewer') {
      user.sentryViewer = !user.sentryViewer;
    }
    if (permission === 'sentryAdmin') {
      user.sentryAdmin = !user.sentryAdmin;
    }

    replaceUser(user);
  };

  const formatData = (cell: any, row: any, permission: string) => {
    const isCheckboxDisabled =
      !orgHasWorkOrderFeatureFlag &&
      (permission === 'canManageWorkOrders' ||
        permission === 'canCompleteWorkOrders');

    return (
      <Form.Check
        type="checkbox"
        checked={row.permittance[permission]}
        onChange={() => {
          changePermissions(row.permittance.userId, permission);
        }}
        id={permission.concat(row.id)}
        key={permission.concat(row.id)}
        disabled={permissionButtonMode === SAVING || isCheckboxDisabled}
        label=""
        custom
      />
    );
  };

  /**This function is for showing the modal, not the actual api call.*/
  const onRemoveUser = (user: OrgUser) => {
    setShowDeleteUserModal(true);
    setSelectedUser(user);
  };

  const calcAddUserProps = () => {
    let userCardProps = {
      validationState: '',
      inputDisabled: false,
      buttonDisabled: false,
      label: (
        <FormattedMessage
          id="app.orgeditpage.add_user_org"
          defaultMessage="Add user to your organization"
          description="Add user org button"
        />
      ),
      buttonStyle: 'default',
      buttonText: (
        <FormattedMessage
          id="app.orgeditpage.add_user"
          defaultMessage="Add User"
          description="Add user button"
        />
      ),
    };

    // user saved successfully
    if (addUserLoading) {
      userCardProps.inputDisabled = true;
      userCardProps.buttonDisabled = true;
      userCardProps.buttonStyle = 'info';
      userCardProps.buttonText = <div>Saving...</div>;
    }

    return userCardProps;
  };

  const PermissionButton = () => {
    let buttonStyles = {
      text: 'Save Permissions',
      disabled: false,
    };
    if (permissionButtonMode === SAVING) {
      buttonStyles.text = 'Saving...';
      buttonStyles.disabled = true;
    }

    return (
      <div style={{ margin: '12px 0px 40px', maxWidth: 400 }}>
        <RaptorButton
          onClick={savePermissions}
          disabled={buttonStyles.disabled}
        >
          {buttonStyles.text}
        </RaptorButton>
      </div>
    );
  };

  // Sentry permissions save button
  const SentrySaveButton = () => {
    let state = {
      text: 'Save Sentry Permissions',
    };

    if (sentryButtonMode === SAVING) {
      state.text = 'Saving...';
    }

    return (
      <div style={{ margin: '12px 0px 40px', maxWidth: 400 }}>
        <RaptorButton onClick={saveSentryPermissions}>
          {state.text}
        </RaptorButton>
      </div>
    );
  };

  const userCardProps = calcAddUserProps();

  return (
    <div className="org-view">
      <Container fluid>
        <Row style={{ paddingTop: '20px' }}>
          <Col xs={12} sm={4} md={4} style={{ paddingLeft: '0' }}>
            <Card>
              <Card.Header>{org.name}</Card.Header>
              <ListGroup>
                <ListGroupItem>
                  <strong>
                    <FormattedMessage
                      id="app.orgeditpage.no_of_users"
                      defaultMessage="Number of Users"
                      description="Number of users display"
                    />
                    &nbsp;
                  </strong>
                  {users.length}
                </ListGroupItem>
              </ListGroup>
            </Card>
          </Col>
          <Col sm={8} md={8} />
        </Row>
        <Row style={{ flexDirection: 'column', paddingTop: '15px' }}>
          <Tabs id="permissions-tabs">
            <Tab
              eventKey={1}
              title={
                <FormattedMessage
                  id="app.orgeditpage.solar_report_permissions"
                  defaultMessage="Solar Report Permissions"
                  description="Solar report permissions tab"
                />
              }
            >
              <FarmPermittance
                users={users}
                formatData={formatData}
                onRemoveUser={onRemoveUser}
                orgId={org.id}
              />
              <PermissionButton />
            </Tab>
            <Tab
              eventKey={2}
              title={
                <FormattedMessage
                  id="app.orgeditpage.image_set_permissions"
                  defaultMessage="Image Set Permissions"
                  description="Image Set permissions tab"
                />
              }
            >
              <JobPermittance
                users={users}
                formatData={formatData}
                onRemoveUser={onRemoveUser}
              />
              <PermissionButton />
            </Tab>
            {isSentryTabEnabled ? (
              <Tab eventKey={3} title="Sentry Command">
                <SentryPermittance
                  users={users}
                  onRemoveUser={onRemoveUser}
                  changePermissions={toggleSentryPermission}
                  isLoading={props.isLoading}
                />
                <SentrySaveButton />
              </Tab>
            ) : null}
          </Tabs>
        </Row>
        <Row>
          <Col xs={12} sm={4} md={4} style={{ paddingLeft: '0' }}>
            <Card
              key="addUserCard"
              style={{ padding: '15px', marginBottom: '15px' }}
            >
              <Form onSubmit={addUser}>
                <strong>
                  <Form.Label>{userCardProps.label}</Form.Label>
                </strong>
                <Form.Group controlId="addUserEmail">
                  <Form.Control
                    onChange={event => {
                      setAddedUserEmail(event.target.value);
                    }}
                    value={addedUserEmail}
                    type="email"
                    disabled={addUserLoading}
                    placeholder="Enter email address"
                  />
                </Form.Group>
                <Button
                  disabled={!addedUserEmail || addUserLoading}
                  type="submit"
                  style={{ width: '100%' }}
                >
                  {userCardProps.buttonText}
                </Button>
              </Form>
            </Card>
          </Col>
          <Col sm={8} md={8} />
        </Row>
      </Container>

      {/* Modal that shows when the user un-checks the view all box */}
      <RemoveUserAll
        user={selectedUser}
        show={showRemoveAllModal}
        onHide={() => {
          setShowRemoveAllModal(false);
        }}
      />

      {/*Modal that warns user when deleting another user from the org.
          Since it depends heavily on the state of this component, I kept it in this file. */}
      <DeleteWarningModal
        show={showDeleteUserModal}
        toggle={() => {
          setShowDeleteUserModal(false);
        }}
        header={
          <FormattedMessage
            id="app.orgeditpage.warning"
            defaultMessage="Warning"
            description="Warning modal title"
          />
        }
        body={
          <FormattedMessage
            id="app.orgeditpage.permanently_remove"
            values={{ name: selectedUser ? selectedUser.email : '' }}
            defaultMessage={`Are you sure you would like to permanently remove {name} from your organization`}
          />
        }
        deleteItem={removeUser}
      />
    </div>
  );
};

export default OrganizationEdit;
